const STEPS = [
  {
    n: "01",
    t: "Connect",
    d: "Add our bot to Slack and Discord with one click each. No apps to build, no tokens to paste.",
  },
  {
    n: "02",
    t: "Scan & choose",
    d: "We scan your workspace and show every channel with its message count and media size. Pick exactly what moves.",
  },
  {
    n: "03",
    t: "Price & pay",
    d: "Toggle channels, content, and time range — the quote updates live. Pay once via Stripe. No seats, no subscription.",
  },
  {
    n: "04",
    t: "Migrate & watch",
    d: "Our engine does the heavy lifting. Messages, threads, reactions and files cross over — live on your dashboard.",
  },
];

export function HowItWorks() {
  return (
    <section className="section" id="how">
      <div className="container">
        <span className="eyebrow">The path</span>
        <h2 className="h-section" style={{ marginTop: 14, maxWidth: "18ch" }}>
          Four steps from Slack history to a living Discord.
        </h2>
        <div className="steps">
          {STEPS.map((s) => (
            <div className="step" key={s.n}>
              <span className="step-n">{s.n}</span>
              <h3>{s.t}</h3>
              <p>{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ByteBoundary() {
  return (
    <section className="section" id="boundary">
      <div className="container boundary">
        <div>
          <span className="eyebrow">Built for scale</span>
          <h2 className="h-section" style={{ margin: "14px 0 18px" }}>
            The price you see is the price — at any volume.
          </h2>
          <p className="lede" style={{ maxWidth: "50ch" }}>
            Media is the expensive part of any migration, so we engineered our
            pipeline to keep your cost predictable no matter how much you move —
            the quote you get is what you pay, whether it's 10 GB or 10 TB. Every
            migration runs fully isolated, and you watch it happen in real time.
          </p>
          <ul className="plan-features" style={{ marginTop: 20 }}>
            <li>Your quote is your price — no surprise overages</li>
            <li>Fully isolated per organization — no one sees another's data</li>
            <li>Live progress, start to finish</li>
          </ul>
        </div>
        <div className="card boundary-diagram">
          <div className="lane bytes">
            <span className="lane-ico" aria-hidden="true">
              📦
            </span>
            Slack → Slackcord → Discord
            <span className="tag">your data</span>
          </div>
          <div style={{ textAlign: "center", color: "var(--ink-faint)" }}>
            <span className="mono" style={{ fontSize: "0.78rem" }}>
              ── secure &amp; isolated ──
            </span>
          </div>
          <div className="lane control">
            <span className="lane-ico" aria-hidden="true">
              📊
            </span>
            Live progress, every channel
            <span className="tag">real-time</span>
          </div>
          <div className="lane">
            <span className="lane-ico" aria-hidden="true">
              🖥️
            </span>
            Watch it on your dashboard
            <span className="tag">live</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export function MediaEducation() {
  return (
    <section className="section" id="media">
      <div className="container">
        <span className="eyebrow">Storage &amp; media, explained</span>
        <h2 className="h-section" style={{ margin: "14px 0 6px", maxWidth: "26ch" }}>
          Unlimited storage, free forever. The only cap is per-file size.
        </h2>
        <p className="lede" style={{ marginBottom: 28, maxWidth: "62ch" }}>
          Once your history is in Discord it stays there — <b>free, forever</b>. No
          storage fees, no retention tiers (unlike Slack, which hides your history
          on lower plans). The one limit is how big each <em>single</em> file can
          be — and a Server Boost lifts that per-file ceiling for your whole team.
        </p>
        <div className="edu-grid">
          <div className="edu-card">
            <span className="edu-tag mono">Free · Level 0–1</span>
            <h3>Messages &amp; everyday photos</h3>
            <p>
              On free Discord we migrate every file up to <b>10 MB each</b> —
              perfect for your whole message history and day-to-day images.
              Storage is unlimited and costs nothing.
            </p>
          </div>
          <div className="edu-card edu-card-featured">
            <span className="edu-tag mono">Boost · bigger files</span>
            <h3>Migrating larger videos?</h3>
            <p>
              Boost your Discord server and we bring over bigger files:{" "}
              <b>Level 2 (~$35/mo) → up to 50 MB</b> each,{" "}
              <b>Level 3 (~$70/mo) → up to 100 MB</b> each. The boost raises the
              per-file size for your whole team going forward — it doesn’t “add
              storage” (storage is already unlimited).
            </p>
          </div>
          <div className="edu-card">
            <span className="edu-tag mono">Over 100 MB</span>
            <h3>Bigger than 100 MB?</h3>
            <p>
              Too large for Discord at any boost level. We host + link those files
              at <b>$1/GB per month</b>, or skip them — your call, per workspace.
            </p>
          </div>
        </div>
        <p className="hero-note" style={{ marginTop: 18 }}>
          To be clear: a Boost lifts the size of each individual file we can
          migrate — not your storage, which is unlimited and free. Boosts are paid
          to Discord; over-100 MB hosting is our optional $1/GB/mo service.
        </p>
      </div>
    </section>
  );
}

export function DiscordCosts() {
  return (
    <section className="section" id="discord-costs">
      <div className="container">
        <span className="eyebrow">Transparency</span>
        <h2 className="h-section" style={{ margin: "14px 0 10px", maxWidth: "20ch" }}>
          Discord is free. We don’t bill you monthly.
        </h2>
        <p className="lede" style={{ marginBottom: 28 }}>
          You pay us once for the migration — that’s it. For full transparency,
          here’s what Discord itself offers (optional, paid to Discord, not us):
        </p>
        <div className="cost-grid">
          <div className="cost-card">
            <b>Discord</b>
            <span className="cost-amt gradient-text">$0</span>
            <p>Servers, unlimited messages, voice and video — free, forever.</p>
          </div>
          <div className="cost-card">
            <b>Server Boosts</b>
            <span className="cost-amt">~$4.99/mo</span>
            <p>
              Optional. Raises your server’s per-file upload limit to 50 MB
              (Level 2) or 100 MB (Level 3) so larger media migrates. Paid to
              Discord.
            </p>
          </div>
          <div className="cost-card">
            <b>Nitro</b>
            <span className="cost-amt">~$9.99/mo</span>
            <p>
              Optional, personal perks. Note: Nitro does <b>not</b> change
              migration upload limits — only server boosts do.
            </p>
          </div>
        </div>
        <p className="hero-note" style={{ marginTop: 18 }}>
          Prices set by Discord and may change. We host nothing recurring — your
          migrated media is uploaded natively to Discord and stays there.
        </p>
      </div>
    </section>
  );
}

const ROADMAP = [
  {
    t: "Managed team onboarding",
    d: "Add, remove, and organize your team in Discord without wrestling its admin UI — and invite everyone at once. Pricing to be announced.",
  },
  {
    t: "Canvases",
    d: "Migrate Slack Canvases as collaborative Google Docs (or Discord forum posts).",
  },
  {
    t: "Bookmarks",
    d: "Bring your channel bookmarks over as pinned links.",
  },
];

export function Roadmap() {
  return (
    <section className="section" id="roadmap">
      <div className="container">
        <span className="eyebrow">Roadmap</span>
        <h2 className="h-section" style={{ margin: "14px 0 8px", maxWidth: "22ch" }}>
          Coming soon
        </h2>
        <p className="lede" style={{ marginBottom: 28 }}>
          On the way — not billed yet. Everything below is in development.
        </p>
        <div className="roadmap-grid">
          {ROADMAP.map((r) => (
            <div className="card roadmap-item" key={r.t}>
              <span className="soon-tag">Coming soon</span>
              <h3 className="roadmap-item-t">{r.t}</h3>
              <p className="muted">{r.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ScoreboardPreview() {
  return (
    <section className="section" id="scoreboard">
      <div className="container boundary">
        <div className="card discord-embed" style={{ order: 2 }}>
          <div className="embed-head">
            <span className="tenant-badge">S</span>
            <div>
              <div className="embed-title">Slackcord · Migration status</div>
              <div className="mono" style={{ fontSize: "0.76rem", color: "var(--ink-faint)" }}>
                updates every few seconds
              </div>
            </div>
            <span className="dot-live" style={{ marginLeft: "auto" }} />
          </div>
          <div className="score">
            {[
              { name: "#engineering", pct: 82 },
              { name: "#design", pct: 64 },
              { name: "#watercooler", pct: 28 },
            ].map((c) => (
              <div className="score-row" key={c.name}>
                <span className="score-name">{c.name}</span>
                <span className="meter animate">
                  <span style={{ width: `${c.pct}%` }} />
                </span>
                <span className="score-pct">{c.pct}%</span>
              </div>
            ))}
          </div>
          <div className="embed-btns">
            <span className="embed-btn">Details</span>
            <span className="embed-btn">Pause</span>
            <span className="embed-btn">Open web dashboard</span>
          </div>
        </div>
        <div style={{ order: 1 }}>
          <span className="eyebrow">Live status</span>
          <h2 className="h-section" style={{ margin: "14px 0 18px" }}>
            Watch it happen — right inside Discord.
          </h2>
          <p className="lede" style={{ maxWidth: "50ch" }}>
            A pinned embed in your server updates itself as the migration runs:
            per-channel progress, totals, and an ETA, with buttons to pause,
            resume, or jump to the web dashboard. Your whole team sees progress
            without leaving Discord.
          </p>
          <p className="hero-note" style={{ marginTop: 16 }}>
            Preview shown. The live embed appears once your migration starts.
          </p>
        </div>
      </div>
    </section>
  );
}
